// src/pages/PasswordRecovery/RecoveryVerifyEmailOtp.tsx
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from '@tanstack/react-router';

import {
  selectAuthError,
  selectAuthStatus,
  selectForgotPasswordEmailAddress,
} from '@/store/selectors/auth';
import { useAppDispatch } from '@/store';
import RecoveryVerifyEmailOtpForm from '@/components/organism/PasswordRecovery/RecoveryVerifyEmailOtpForm';
import { verifyRecoveryOtpThunk } from '@/store/thunks/auth/verifyRecoveryOtpThunk';
import { verifyRecoveryTwoFactorThunk } from '@/store/thunks/auth/verifyRecoveryTwoFactorThunk';
import { MIN_LEN, PHONE_VERIFICATION_BYPASS_CODE } from '@/constants';
import { isPhoneVerificationBypassToken } from '@/utils/phoneVerificationBypass';

const OTP_LENGTH = 6;

export default function RecoveryVerifyEmailOtpPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const error = useSelector(selectAuthError);
  const emailAddress = useSelector(selectForgotPasswordEmailAddress);

  const handleSubmit = React.useCallback(
    async (code: string): Promise<void> => {
      if (code.trim().length !== OTP_LENGTH) return;
      if (typeof emailAddress !== 'string' || emailAddress.trim().length === MIN_LEN) return;

      try {
        const result = await dispatch(verifyRecoveryOtpThunk({ emailAddress, code })).unwrap();

        if (isPhoneVerificationBypassToken(result.recoveryToken)) {
          await dispatch(
            verifyRecoveryTwoFactorThunk({ code: PHONE_VERIFICATION_BYPASS_CODE })
          ).unwrap();
          void navigate({ to: '/reset-password/set-password' });
          return;
        }

        void navigate({ to: '/reset-password/two-factor-verify' });
      } catch {
        // error handled in redux
      }
    },
    [dispatch, emailAddress, navigate]
  );

  return (
    <RecoveryVerifyEmailOtpForm
      error={error}
      onSubmit={(code) => {
        void handleSubmit(code);
      }}
    />
  );
}
